import React, { useMemo, useState } from "react";
import {
  AbsoluteFill,
  Audio,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Easing,
  spring,
  Sequence,
} from "remotion";
import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { slide } from "@remotion/transitions/slide";
import { fade } from "@remotion/transitions/fade";
import { z } from "zod";
import { ImageSlideshow } from "../Compostion/ImageSlideshow";
import { DynamicImageGrid } from "../Compostion/DynamicImageGrid";
import CroppedImg from "../components/CroppedImg";
import CroppedVideo from "../components/CroppedVideo";
import { resolveMediaSrc } from "../utils/mediaSrc";

/* ── Media helpers ──────────────────────────────────────────── */
const getMediaSrc = (src) => resolveMediaSrc(src);

const isVideoSrc = (src) => {
  if (!src || typeof src !== "string") return false;
  const clean = src.split(/[?#]/)[0];
  return /\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/i.test(clean);
};

const resolveMediaType = (item) => {
  if (item?.type === "video" || item?.type === "image") return item.type;
  return isVideoSrc(item?.src) ? "video" : "image";
};

// ─── Zod Schema ───────────────────────────────────────────────────────────────

const ReviewItemSchema = z.object({
  reviewer: z.string().default("Customer"),
  text: z.string().default("Great product!"),
  rating: z.number().min(1).max(5).default(5),
});

const FeatureItemSchema = z.object({
  icon: z.string().default("✓"),
  label: z.string().default("Feature"),
});

const CropSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  sourceWidth: z.number().optional(),
  sourceHeight: z.number().optional(),
}).nullable().optional();

const MediaItemSchema = z.object({
  src: z.string().describe("URL or staticFile path for a video or image"),
  type: z.enum(["video", "image"]).optional().describe("Explicit media type; auto-detected from extension if omitted"),
  crop: CropSchema.describe("Optional crop rect"),
  videoStartFrom: z.number().default(0).describe("For video: start playback from this frame"),
  videoEndAt: z.number().optional().describe("For video: stop playback at this frame"),
});

export const AmazonShowcasePropsSchema = z.object({
  // Product info
  productTitle: z
    .string()
    .default("Premium Wireless Headphones")
    .describe("Product name displayed in the hero"),
  productSubtitle: z
    .string()
    .default("Noise Cancelling | 40hr Battery | Hi-Res Audio")
    .describe("Short tagline under the title"),
  price: z.string().default("$79.99").describe("Display price"),
  originalPrice: z
    .string()
    .default("$129.99")
    .describe("Crossed-out original price"),
  rating: z
    .number()
    .min(0)
    .max(5)
    .default(4.7)
    .describe("Star rating (0-5, supports decimals)"),
  reviewCount: z
    .string()
    .default("12,847 ratings")
    .describe("Review count text"),
  heroImage: z.string().default("").describe("Main hero product image URL"),
  heroCrop: CropSchema.describe("Optional crop rect for hero image"),

  // Feature highlights
  features: z
    .array(FeatureItemSchema)
    .default([
      { icon: "🎧", label: "Active Noise Cancelling" },
      { icon: "🔋", label: "40-Hour Battery Life" },
      { icon: "🎵", label: "Hi-Res Audio Certified" },
      { icon: "📱", label: "Multipoint Connection" },
      { icon: "🎤", label: "Crystal Clear Calls" },
      { icon: "💧", label: "IPX4 Water Resistant" },
    ])
    .describe("Feature list with emoji icons"),

  // Customer reviews
  reviews: z
    .array(ReviewItemSchema)
    .default([
      {
        reviewer: "Sarah M.",
        text: "Best headphones I've ever owned. The noise cancelling is incredible!",
        rating: 5,
      },
      {
        reviewer: "Mike T.",
        text: "Great sound quality for the price. Battery lasts forever.",
        rating: 5,
      },
      {
        reviewer: "Jessica L.",
        text: "Very comfortable for long listening sessions. Highly recommend!",
        rating: 4,
      },
    ])
    .describe("Customer review quotes"),

  // Gallery images (reuse ImageSlideshow)
  galleryImages: z
    .array(MediaItemSchema)
    .default([])
    .describe("Product gallery media items"),

  // Grid images (reuse DynamicImageGrid)
  gridImages: z
    .array(MediaItemSchema)
    .default([])
    .describe("Media items for the final grid montage"),

  // Verdict
  verdictScore: z
    .string()
    .default("9.4")
    .describe("Final score out of 10"),
  verdictLabel: z
    .string()
    .default("HIGHLY RECOMMENDED")
    .describe("Verdict label"),
  prosText: z
    .array(z.string())
    .default(["Excellent sound", "Great battery", "Comfortable fit"])
    .describe("Pros list"),
  consText: z
    .array(z.string())
    .default(["No wired mode", "Case is bulky"])
    .describe("Cons list"),

  // Timing (in frames at 30fps)
  heroDuration: z.number().default(210).describe("Hero intro duration"),
  featureDuration: z.number().default(240).describe("Feature section duration"),
  reviewsDuration: z.number().default(240).describe("Reviews section duration"),
  galleryDuration: z.number().default(240).describe("Gallery section duration"),
  verdictDuration: z.number().default(210).describe("Verdict section duration"),
  transitionDuration: z
    .number()
    .default(25)
    .describe("Transition duration between sections"),

  // Include flags
  includeGallery: z
    .boolean()
    .default(true)
    .describe("Include gallery slideshow section"),
  includeGrid: z
    .boolean()
    .default(false)
    .describe("Include grid montage section"),

  // Audio
  audioSrc: z.string().optional().describe("Background music file path or URL"),
  audioVolume: z.number().min(0).max(1).default(0.5),
  audioStartFrom: z.number().default(0).describe("Audio start offset in seconds"),
  audioDurationOverride: z
    .number()
    .default(60)
    .describe("Total audio/video duration in seconds"),

  // Theming
  accentColor: z.string().default("#FF9900").describe("Amazon orange accent"),
  darkColor: z.string().default("#131921").describe("Amazon dark background"),
  lightColor: z.string().default("#FFFFFF"),
  starColor: z.string().default("#FFA41C").describe("Star rating color"),
  priceColor: z.string().default("#B12704").describe("Amazon price red"),
});

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Animated star rating row */
const StarRating = ({ rating, color, size, frame, fps, delay = 0 }) => {
  const fullStars = Math.floor(rating);
  const partial = rating - fullStars;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: size * 0.15 }}>
      {Array.from({ length: 5 }).map((_, i) => {
        const starSpring = spring({
          fps,
          frame: frame - delay - i * 3,
          config: { damping: 10, stiffness: 180, mass: 0.4 },
        });
        const scale = interpolate(starSpring, [0, 1], [0, 1], {
          extrapolateRight: "clamp",
        });
        const fill = i < fullStars ? 1 : i === fullStars ? partial : 0;

        return (
          <div
            key={i}
            style={{
              width: size,
              height: size,
              transform: `scale(${scale})`,
              position: "relative",
            }}
          >
            {/* Empty star */}
            <svg
              viewBox="0 0 24 24"
              width={size}
              height={size}
              style={{ position: "absolute", top: 0, left: 0 }}
            >
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"
                fill="#e0e0e0"
                stroke="none"
              />
            </svg>
            {/* Filled star with clip */}
            <svg
              viewBox="0 0 24 24"
              width={size}
              height={size}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                clipPath: `inset(0 ${(1 - fill) * 100}% 0 0)`,
              }}
            >
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"
                fill={color}
                stroke="none"
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
};

/** Animated price tag */
const PriceTag = ({ price, originalPrice, priceColor, frame, fps, delay = 0 }) => {
  const priceSpring = spring({
    fps,
    frame: frame - delay,
    config: { damping: 12, stiffness: 120, mass: 0.6 },
  });
  const scale = interpolate(priceSpring, [0, 1], [0.3, 1], {
    extrapolateRight: "clamp",
  });
  const opacity = interpolate(priceSpring, [0, 1], [0, 1], {
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 20,
        transform: `scale(${scale})`,
        opacity,
        transformOrigin: "left center",
      }}
    >
      <span
        style={{
          fontSize: 96,
          fontWeight: 800,
          color: priceColor,
          fontFamily:
            "'Amazon Ember', -apple-system, BlinkMacSystemFont, sans-serif",
          letterSpacing: "-0.03em",
        }}
      >
        {price}
      </span>
      {originalPrice && (
        <span
          style={{
            fontSize: 48,
            fontWeight: 400,
            color: "#999",
            textDecoration: "line-through",
            fontFamily:
              "'Amazon Ember', -apple-system, BlinkMacSystemFont, sans-serif",
          }}
        >
          {originalPrice}
        </span>
      )}
    </div>
  );
};

/** Amazon-style "verified" badge */
const VerifiedBadge = ({ frame, fps, delay = 0 }) => {
  const s = spring({
    fps,
    frame: frame - delay,
    config: { damping: 12, stiffness: 200, mass: 0.5 },
  });

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 18px",
        borderRadius: 30,
        background: "#067D62",
        transform: `scale(${interpolate(s, [0, 1], [0, 1], { extrapolateRight: "clamp" })})`,
        opacity: interpolate(s, [0, 1], [0, 1], {
          extrapolateRight: "clamp",
        }),
      }}
    >
      <svg width={22} height={22} viewBox="0 0 24 24" fill="#fff">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
      </svg>
      <span
        style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 22,
          fontFamily: "sans-serif",
          letterSpacing: "0.04em",
        }}
      >
        Verified Purchase
      </span>
    </div>
  );
};

// ─── Sequence Scenes ──────────────────────────────────────────────────────────

/**
 * SCENE 1 — Hero Product Intro
 * Dramatic product image with animated title, rating, price
 */
const HeroScene = ({
  productTitle,
  productSubtitle,
  heroImage,
  heroCrop,
  price,
  originalPrice,
  rating,
  reviewCount,
  accentColor,
  darkColor,
  starColor,
  priceColor,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Image source helper
  const imgSrc = useMemo(() => {
    if (!heroImage) return null;
    return getMediaSrc(heroImage);
  }, [heroImage]);

  // Background gradient animation
  const gradientShift = interpolate(frame, [0, 300], [0, 30], {
    extrapolateRight: "clamp",
  });

  // Product image entrance
  const imgSpring = spring({
    fps,
    frame: frame - 10,
    config: { damping: 14, stiffness: 80, mass: 0.8 },
  });
  const imgScale = interpolate(imgSpring, [0, 1], [0.6, 1], {
    extrapolateRight: "clamp",
  });
  const imgOpacity = interpolate(imgSpring, [0, 1], [0, 1], {
    extrapolateRight: "clamp",
  });

  // Title entrance
  const titleSpring = spring({
    fps,
    frame: frame - 35,
    config: { damping: 15, stiffness: 100, mass: 0.6 },
  });

  // Floating shimmer on product image
  const shimmerX = interpolate(frame, [0, 90, 180], [-200, 600, -200], {
    extrapolateRight: "extend",
  });

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(${135 + gradientShift}deg, ${darkColor} 0%, #1a2332 40%, ${darkColor} 100%)`,
        overflow: "hidden",
      }}
    >
      {/* Subtle animated background pattern */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${accentColor}15 0%, transparent 50%), 
                            radial-gradient(circle at 80% 20%, ${accentColor}10 0%, transparent 40%)`,
          opacity: interpolate(frame, [0, 60], [0, 1], {
            extrapolateRight: "clamp",
          }),
        }}
      />

      {/* Product image — right side */}
      {imgSrc && (
        <div
          style={{
            position: "absolute",
            right: 60,
            top: "50%",
            transform: `translateY(-50%) scale(${imgScale})`,
            opacity: imgOpacity,
            width: width * 0.42,
            height: height * 0.7,
            borderRadius: 32,
            overflow: "hidden",
            boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 0 2px ${accentColor}40`,
          }}
        >
          <CroppedImg
            src={imgSrc}
            crop={heroCrop}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            containerStyle={{
              width: "100%",
              height: "100%",
            }}
          />
          {/* Shimmer overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)`,
              transform: `translateX(${shimmerX}px)`,
            }}
          />
        </div>
      )}

      {/* Text content — left side */}
      <div
        style={{
          position: "absolute",
          left: 80,
          top: "50%",
          transform: "translateY(-50%)",
          width: imgSrc ? width * 0.45 : width * 0.85,
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        {/* Amazon badge */}
        <div
          style={{
            opacity: interpolate(
              spring({
                fps,
                frame: frame - 20,
                config: { damping: 15, stiffness: 150 },
              }),
              [0, 1],
              [0, 1],
              { extrapolateRight: "clamp" }
            ),
            transform: `translateX(${interpolate(
              spring({
                fps,
                frame: frame - 20,
                config: { damping: 15, stiffness: 150 },
              }),
              [0, 1],
              [-40, 0],
              { extrapolateRight: "clamp" }
            )}px)`,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: accentColor,
              padding: "10px 24px",
              borderRadius: 8,
            }}
          >
            <span
              style={{
                color: darkColor,
                fontWeight: 900,
                fontSize: 26,
                letterSpacing: "0.06em",
                fontFamily: "sans-serif",
              }}
            >
              ★ PRODUCT REVIEW
            </span>
          </div>
        </div>

        {/* Product title */}
        <h1
          style={{
            fontSize: imgSrc ? 68 : 80,
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.1,
            margin: 0,
            letterSpacing: "-0.02em",
            fontFamily:
              "'Amazon Ember', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            opacity: interpolate(titleSpring, [0, 1], [0, 1], {
              extrapolateRight: "clamp",
            }),
            transform: `translateY(${interpolate(titleSpring, [0, 1], [50, 0], { extrapolateRight: "clamp" })}px)`,
          }}
        >
          {productTitle}
        </h1>

        {/* Subtitle */}
        <p
          style={{
            fontSize: 32,
            fontWeight: 400,
            color: "#aabbcc",
            margin: 0,
            lineHeight: 1.4,
            fontFamily: "sans-serif",
            opacity: interpolate(
              spring({
                fps,
                frame: frame - 50,
                config: { damping: 15, stiffness: 100 },
              }),
              [0, 1],
              [0, 1],
              { extrapolateRight: "clamp" }
            ),
            transform: `translateY(${interpolate(
              spring({
                fps,
                frame: frame - 50,
                config: { damping: 15, stiffness: 100 },
              }),
              [0, 1],
              [30, 0],
              { extrapolateRight: "clamp" }
            )}px)`,
          }}
        >
          {productSubtitle}
        </p>

        {/* Star rating row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            opacity: interpolate(
              spring({
                fps,
                frame: frame - 60,
                config: { damping: 15, stiffness: 100 },
              }),
              [0, 1],
              [0, 1],
              { extrapolateRight: "clamp" }
            ),
          }}
        >
          <StarRating
            rating={rating}
            color={starColor}
            size={44}
            frame={frame}
            fps={fps}
            delay={60}
          />
          <span
            style={{
              color: starColor,
              fontSize: 40,
              fontWeight: 700,
              fontFamily: "sans-serif",
            }}
          >
            {rating}
          </span>
          <span
            style={{
              color: "#8899aa",
              fontSize: 28,
              fontFamily: "sans-serif",
            }}
          >
            {reviewCount}
          </span>
        </div>

        {/* Price */}
        <PriceTag
          price={price}
          originalPrice={originalPrice}
          priceColor={priceColor}
          frame={frame}
          fps={fps}
          delay={75}
        />
      </div>

      {/* Bottom accent bar */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 6,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}80, ${accentColor})`,
          transform: `scaleX(${interpolate(frame, [0, 60], [0, 1], { extrapolateRight: "clamp" })})`,
          transformOrigin: "left",
        }}
      />
    </AbsoluteFill>
  );
};

/**
 * SCENE 2 — Feature Highlights
 * Animated list of features staggering in with icons
 */
const FeaturesScene = ({ features, accentColor, darkColor }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(160deg, #0f1923 0%, ${darkColor} 50%, #0f1923 100%)`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: 80,
      }}
    >
      {/* Section header */}
      <div
        style={{
          opacity: interpolate(
            spring({
              fps,
              frame,
              config: { damping: 15, stiffness: 120 },
            }),
            [0, 1],
            [0, 1],
            { extrapolateRight: "clamp" }
          ),
          transform: `translateY(${interpolate(
            spring({
              fps,
              frame,
              config: { damping: 15, stiffness: 120 },
            }),
            [0, 1],
            [-30, 0],
            { extrapolateRight: "clamp" }
          )}px)`,
          marginBottom: 60,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: accentColor,
            letterSpacing: "0.15em",
            fontFamily: "sans-serif",
            marginBottom: 12,
          }}
        >
          WHY YOU'LL LOVE IT
        </div>
        <div
          style={{
            width: 80,
            height: 4,
            background: accentColor,
            borderRadius: 2,
            margin: "0 auto",
          }}
        />
      </div>

      {/* Feature grid — 2 columns */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 30,
          maxWidth: width * 0.85,
        }}
      >
        {features.map((feature, i) => {
          const delay = 25 + i * 12;
          const featureSpring = spring({
            fps,
            frame: frame - delay,
            config: { damping: 12, stiffness: 130, mass: 0.5 },
          });
          const scale = interpolate(featureSpring, [0, 1], [0.5, 1], {
            extrapolateRight: "clamp",
          });
          const opacity = interpolate(featureSpring, [0, 1], [0, 1], {
            extrapolateRight: "clamp",
          });

          return (
            <div
              key={i}
              style={{
                width: width * 0.38,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${accentColor}30`,
                borderRadius: 20,
                padding: "32px 36px",
                display: "flex",
                alignItems: "center",
                gap: 24,
                transform: `scale(${scale})`,
                opacity,
                boxShadow: `0 4px 20px rgba(0,0,0,0.3)`,
              }}
            >
              <span style={{ fontSize: 52, lineHeight: 1 }}>
                {feature.icon}
              </span>
              <span
                style={{
                  fontSize: 34,
                  fontWeight: 600,
                  color: "#fff",
                  fontFamily: "sans-serif",
                  lineHeight: 1.3,
                }}
              >
                {feature.label}
              </span>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

/**
 * SCENE 3 — Customer Reviews
 * Animated review cards with star ratings
 */
const ReviewsScene = ({ reviews, accentColor, darkColor, starColor }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(145deg, #fafafa 0%, #f0f0f0 100%)`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: 80,
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: 50,
          textAlign: "center",
          opacity: interpolate(
            spring({
              fps,
              frame,
              config: { damping: 15, stiffness: 120 },
            }),
            [0, 1],
            [0, 1],
            { extrapolateRight: "clamp" }
          ),
        }}
      >
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: darkColor,
            letterSpacing: "0.12em",
            fontFamily: "sans-serif",
            marginBottom: 8,
          }}
        >
          WHAT CUSTOMERS SAY
        </div>
        <div
          style={{
            width: 80,
            height: 4,
            background: accentColor,
            borderRadius: 2,
            margin: "0 auto",
          }}
        />
      </div>

      {/* Review cards */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
          width: width * 0.8,
        }}
      >
        {reviews.map((review, i) => {
          const delay = 20 + i * 25;
          const cardSpring = spring({
            fps,
            frame: frame - delay,
            config: { damping: 13, stiffness: 110, mass: 0.5 },
          });
          const slideX = interpolate(
            cardSpring,
            [0, 1],
            [i % 2 === 0 ? -300 : 300, 0],
            { extrapolateRight: "clamp" }
          );
          const opacity = interpolate(cardSpring, [0, 1], [0, 1], {
            extrapolateRight: "clamp",
          });

          return (
            <div
              key={i}
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: "36px 44px",
                boxShadow:
                  "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                transform: `translateX(${slideX}px)`,
                opacity,
                borderLeft: `5px solid ${accentColor}`,
              }}
            >
              {/* Reviewer + stars */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {/* Avatar circle */}
                  <div
                    style={{
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${accentColor}, #e68a00)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 24,
                      fontFamily: "sans-serif",
                    }}
                  >
                    {review.reviewer.charAt(0)}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: "#222",
                        fontFamily: "sans-serif",
                      }}
                    >
                      {review.reviewer}
                    </div>
                    <VerifiedBadge frame={frame} fps={fps} delay={delay + 15} />
                  </div>
                </div>
                <StarRating
                  rating={review.rating}
                  color={starColor}
                  size={32}
                  frame={frame}
                  fps={fps}
                  delay={delay + 8}
                />
              </div>

              {/* Review text */}
              <p
                style={{
                  fontSize: 30,
                  lineHeight: 1.5,
                  color: "#444",
                  margin: 0,
                  fontStyle: "italic",
                  fontFamily: "Georgia, serif",
                }}
              >
                "{review.text}"
              </p>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

/**
 * SCENE 5 — Final Verdict
 * Big score reveal with pros/cons
 */
const VerdictScene = ({
  verdictScore,
  verdictLabel,
  prosText,
  consText,
  accentColor,
  darkColor,
  starColor,
  rating,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Score counter animation
  const targetScore = parseFloat(verdictScore) || 9.0;
  const counterProgress = interpolate(frame, [20, 80], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const displayScore = (targetScore * counterProgress).toFixed(1);

  // Score ring animation
  const ringProgress = interpolate(frame, [20, 90], [0, targetScore / 10], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });

  // Verdict label entrance
  const labelSpring = spring({
    fps,
    frame: frame - 70,
    config: { damping: 10, stiffness: 100, mass: 0.6 },
  });

  const ringRadius = 130;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringStrokeDashoffset = ringCircumference * (1 - ringProgress);

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(150deg, ${darkColor} 0%, #1a2836 40%, ${darkColor} 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0,
      }}
    >
      {/* "FINAL VERDICT" header */}
      <div
        style={{
          fontSize: 30,
          fontWeight: 700,
          color: accentColor,
          letterSpacing: "0.2em",
          fontFamily: "sans-serif",
          marginBottom: 40,
          opacity: interpolate(
            spring({ fps, frame, config: { damping: 15, stiffness: 120 } }),
            [0, 1],
            [0, 1],
            { extrapolateRight: "clamp" }
          ),
        }}
      >
        FINAL VERDICT
      </div>

      {/* Score ring */}
      <div
        style={{
          position: "relative",
          width: ringRadius * 2 + 40,
          height: ringRadius * 2 + 40,
          marginBottom: 30,
        }}
      >
        <svg
          width={ringRadius * 2 + 40}
          height={ringRadius * 2 + 40}
          style={{ transform: "rotate(-90deg)" }}
        >
          {/* Background ring */}
          <circle
            cx={ringRadius + 20}
            cy={ringRadius + 20}
            r={ringRadius}
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth={16}
          />
          {/* Progress ring */}
          <circle
            cx={ringRadius + 20}
            cy={ringRadius + 20}
            r={ringRadius}
            fill="none"
            stroke={accentColor}
            strokeWidth={16}
            strokeLinecap="round"
            strokeDasharray={ringCircumference}
            strokeDashoffset={ringStrokeDashoffset}
          />
        </svg>
        {/* Score number centered */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontSize: 110,
              fontWeight: 900,
              color: "#fff",
              fontFamily: "sans-serif",
              lineHeight: 1,
            }}
          >
            {displayScore}
          </span>
          <span
            style={{
              fontSize: 26,
              color: "#889",
              fontWeight: 500,
              fontFamily: "sans-serif",
            }}
          >
            / 10
          </span>
        </div>
      </div>

      {/* Stars */}
      <div style={{ marginBottom: 20 }}>
        <StarRating
          rating={rating}
          color={starColor}
          size={48}
          frame={frame}
          fps={fps}
          delay={50}
        />
      </div>

      {/* Verdict label */}
      <div
        style={{
          background: accentColor,
          padding: "14px 48px",
          borderRadius: 12,
          marginBottom: 50,
          transform: `scale(${interpolate(labelSpring, [0, 1], [0, 1], { extrapolateRight: "clamp" })})`,
          opacity: interpolate(labelSpring, [0, 1], [0, 1], {
            extrapolateRight: "clamp",
          }),
        }}
      >
        <span
          style={{
            fontSize: 36,
            fontWeight: 900,
            color: darkColor,
            letterSpacing: "0.1em",
            fontFamily: "sans-serif",
          }}
        >
          {verdictLabel}
        </span>
      </div>

      {/* Pros and Cons */}
      <div
        style={{
          display: "flex",
          gap: 60,
          width: width * 0.8,
          justifyContent: "center",
        }}
      >
        {/* Pros */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: "#4CAF50",
              marginBottom: 20,
              fontFamily: "sans-serif",
              opacity: interpolate(
                spring({
                  fps,
                  frame: frame - 85,
                  config: { damping: 12, stiffness: 120 },
                }),
                [0, 1],
                [0, 1],
                { extrapolateRight: "clamp" }
              ),
            }}
          >
            ✓ PROS
          </div>
          {prosText.map((pro, i) => {
            const proDelay = 90 + i * 10;
            const proSpring = spring({
              fps,
              frame: frame - proDelay,
              config: { damping: 12, stiffness: 130, mass: 0.4 },
            });
            return (
              <div
                key={i}
                style={{
                  fontSize: 28,
                  color: "#ccc",
                  fontFamily: "sans-serif",
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  opacity: interpolate(proSpring, [0, 1], [0, 1], {
                    extrapolateRight: "clamp",
                  }),
                  transform: `translateX(${interpolate(proSpring, [0, 1], [-20, 0], { extrapolateRight: "clamp" })}px)`,
                }}
              >
                <span style={{ color: "#4CAF50", fontWeight: 700 }}>+</span>
                {pro}
              </div>
            );
          })}
        </div>

        {/* Cons */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: "#f44336",
              marginBottom: 20,
              fontFamily: "sans-serif",
              opacity: interpolate(
                spring({
                  fps,
                  frame: frame - 85,
                  config: { damping: 12, stiffness: 120 },
                }),
                [0, 1],
                [0, 1],
                { extrapolateRight: "clamp" }
              ),
            }}
          >
            ✗ CONS
          </div>
          {consText.map((con, i) => {
            const conDelay = 90 + i * 10;
            const conSpring = spring({
              fps,
              frame: frame - conDelay,
              config: { damping: 12, stiffness: 130, mass: 0.4 },
            });
            return (
              <div
                key={i}
                style={{
                  fontSize: 28,
                  color: "#ccc",
                  fontFamily: "sans-serif",
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  opacity: interpolate(conSpring, [0, 1], [0, 1], {
                    extrapolateRight: "clamp",
                  }),
                  transform: `translateX(${interpolate(conSpring, [0, 1], [-20, 0], { extrapolateRight: "clamp" })}px)`,
                }}
              >
                <span style={{ color: "#f44336", fontWeight: 700 }}>−</span>
                {con}
              </div>
            );
          })}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AmazonShowcase = ({
  productTitle = "Premium Wireless Headphones",
  productSubtitle = "Noise Cancelling | 40hr Battery | Hi-Res Audio",
  heroImage = "",
  heroCrop = null,
  price = "$79.99",
  originalPrice = "$129.99",
  rating = 4.7,
  reviewCount = "12,847 ratings",
  features = [],
  reviews = [],
  galleryImages = [],
  gridImages = [],
  verdictScore = "9.4",
  verdictLabel = "HIGHLY RECOMMENDED",
  prosText = [],
  consText = [],
  heroDuration = 210,
  featureDuration = 240,
  reviewsDuration = 240,
  galleryDuration = 240,
  verdictDuration = 210,
  transitionDuration = 25,
  includeGallery = true,
  includeGrid = false,
  audioSrc = undefined,
  audioVolume = 0.5,
  audioStartFrom = 0,
  accentColor = "#FF9900",
  darkColor = "#131921",
  lightColor = "#FFFFFF",
  starColor = "#FFA41C",
  priceColor = "#B12704",
}) => {
  const [audioError, setAudioError] = useState(false);
  const { durationInFrames } = useVideoConfig();

  const isRemoteAudio = (src) =>
    src && (src.startsWith("http://") || src.startsWith("https://"));

  const getAudioSrc = (src) => {
    try {
      return isRemoteAudio(src) ? src : staticFile(src);
    } catch {
      return null;
    }
  };

  const audioSource = audioSrc ? getAudioSrc(audioSrc) : null;

  return (
    <AbsoluteFill>
      {/* Background audio */}
      {audioSource && !audioError && (
        <Audio
          src={audioSource}
          volume={audioVolume}
          startFrom={Math.round(audioStartFrom * 30)}
          onError={(error) => {
            console.warn("AmazonShowcase audio failed to load", error);
            setAudioError(true);
          }}
        />
      )}

      <TransitionSeries>
        {/* 1 — Hero Intro */}
        <TransitionSeries.Sequence
          durationInFrames={heroDuration + transitionDuration}
        >
          <HeroScene
            productTitle={productTitle}
            productSubtitle={productSubtitle}
            heroImage={heroImage}
            heroCrop={heroCrop}
            price={price}
            originalPrice={originalPrice}
            rating={rating}
            reviewCount={reviewCount}
            accentColor={accentColor}
            darkColor={darkColor}
            starColor={starColor}
            priceColor={priceColor}
          />
        </TransitionSeries.Sequence>

        {/* Transition */}
        <TransitionSeries.Transition
          presentation={slide({ direction: "from-right" })}
          timing={linearTiming({ durationInFrames: transitionDuration })}
        />

        {/* 2 — Feature Highlights */}
        <TransitionSeries.Sequence
          durationInFrames={featureDuration + transitionDuration}
        >
          <FeaturesScene
            features={features}
            accentColor={accentColor}
            darkColor={darkColor}
          />
        </TransitionSeries.Sequence>

        {/* Transition */}
        <TransitionSeries.Transition
          presentation={fade()}
          timing={linearTiming({ durationInFrames: transitionDuration })}
        />

        {/* 3 — Customer Reviews */}
        <TransitionSeries.Sequence
          durationInFrames={reviewsDuration + transitionDuration}
        >
          <ReviewsScene
            reviews={reviews}
            accentColor={accentColor}
            darkColor={darkColor}
            starColor={starColor}
          />
        </TransitionSeries.Sequence>

        {/* 4 — Gallery (optional) */}
        {includeGallery && galleryImages.length > 0 && (
          <>
            <TransitionSeries.Transition
              presentation={slide({ direction: "from-bottom" })}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
            <TransitionSeries.Sequence
              durationInFrames={galleryDuration + transitionDuration}
            >
              <AbsoluteFill style={{ background: "#000" }}>
                <ImageSlideshow
                  images={galleryImages.map((item) => {
                    const src = typeof item === 'string' ? item : item.src;
                    return { src };
                  })}
                  transitionDuration={20}
                  enableTransitions={true}
                  panIntensity={0.7}
                  zoomLevel={1.15}
                  visibleTime={Math.max(
                    60,
                    Math.floor(galleryDuration / galleryImages.length)
                  )}
                />
              </AbsoluteFill>
            </TransitionSeries.Sequence>
          </>
        )}

        {/* 4b — Grid montage (optional) */}
        {includeGrid && gridImages.length > 0 && (
          <>
            <TransitionSeries.Transition
              presentation={slide({ direction: "from-left" })}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
            <TransitionSeries.Sequence
              durationInFrames={galleryDuration + transitionDuration}
            >
              <DynamicImageGrid
                images={gridImages.map((item) => typeof item === 'string' ? item : item.src)}
                durationInFrames={galleryDuration}
                revealDuration={40}
                hideDuration={25}
                finalRevealDuration={100}
                gridPadding={20}
                backgroundColor="#111"
                borderRadius={12}
                animationSeed={77}
                finalShuffleCount={3}
                finalShuffleSpeed={5}
              />
            </TransitionSeries.Sequence>
          </>
        )}

        {/* Transition to Verdict */}
        <TransitionSeries.Transition
          presentation={fade()}
          timing={linearTiming({ durationInFrames: transitionDuration })}
        />

        {/* 5 — Final Verdict */}
        <TransitionSeries.Sequence durationInFrames={verdictDuration}>
          <VerdictScene
            verdictScore={verdictScore}
            verdictLabel={verdictLabel}
            prosText={prosText}
            consText={consText}
            accentColor={accentColor}
            darkColor={darkColor}
            starColor={starColor}
            rating={rating}
          />
        </TransitionSeries.Sequence>
      </TransitionSeries>
    </AbsoluteFill>
  );
};

// ─── Metadata Calculator ──────────────────────────────────────────────────────

export const calculateAmazonShowcaseMetadata = async ({ props, defaultProps }) => {
  const merged = { ...defaultProps, ...props };
  const {
    heroDuration,
    featureDuration,
    reviewsDuration,
    galleryDuration,
    verdictDuration,
    transitionDuration,
    includeGallery,
    includeGrid,
    galleryImages,
    gridImages,
    audioDurationOverride,
    audioStartFrom,
  } = merged;

  // Calculate total from sections
  let total =
    heroDuration +
    featureDuration +
    reviewsDuration +
    verdictDuration;

  // Transitions between fixed sections (hero→features, features→reviews, reviews→verdict = 3)
  total += transitionDuration * 3;

  if (includeGallery && galleryImages && galleryImages.length > 0) {
    total += galleryDuration + transitionDuration;
  }

  if (includeGrid && gridImages && gridImages.length > 0) {
    total += galleryDuration + transitionDuration;
  }

  // If audio duration is provided, prefer it
  if (audioDurationOverride) {
    const audioDuration = Math.max(
      0,
      audioDurationOverride - (audioStartFrom || 0)
    );
    const audioFrames = Math.ceil(audioDuration * 30);
    if (audioFrames > 0) {
      total = Math.max(total, audioFrames);
    }
  }

  return {
    durationInFrames: Math.max(90, total),
  };
};

export default AmazonShowcase;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
